import React, { useState, useEffect } from 'react';
import { 
  SparklesIcon,
  ShoppingCartIcon,
  TagIcon,
  ClockIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useCart } from '../../contexts/CartContext';
import toast from 'react-hot-toast';

const FeaturedProductsPanel = ({ onProductClick }) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/featured-products/b2b?limit=6');
      setFeaturedProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdd = async (product) => {
    try {
      const productWithPrice = {
        ...product,
        price: product.price_b2b || product.price_b2c
      };
      
      addToCart(productWithPrice, 1);
      toast.success(`${product.name} נוסף לעגלה!`);
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('שגיאה בהוספה לעגלה');
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-purple-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-white/50 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!featuredProducts.length) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
            <SparklesIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              🔥 מוצרים טרנדים
            </h3>
            <p className="text-sm text-gray-600">
              מוצרים חדשים, מבצעים מיוחדים והזדמנויות עסקיות
            </p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full border border-purple-300">
          <ArrowTrendingUpIcon className="h-4 w-4 text-purple-600" />
          <span className="text-xs font-semibold text-purple-700">
            {featuredProducts.length} מוצרים
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {featuredProducts.map((product) => (
          <div
            key={product.id}
            className="group bg-white rounded-lg border-2 border-gray-200 hover:border-purple-400 p-4 transition-all duration-200 hover:shadow-lg cursor-pointer"
            onClick={() => onProductClick && onProductClick(product)}
          >
            <div className="flex items-start gap-3">
              {/* Product Image */}
              <div className="flex-shrink-0">
                <img
                  src={
                    product.images?.[0]
                      ? product.images[0].startsWith('http')
                        ? product.images[0]
                        : `/api${product.images[0]}`
                      : 'https://picsum.photos/80/80'
                  }
                  alt={product.name}
                  className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200 group-hover:border-purple-300 transition-colors"
                />
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                {/* Product Name */}
                <h4 className="font-semibold text-gray-900 text-sm mb-1 truncate group-hover:text-purple-600 transition-colors">
                  {product.name}
                </h4>

                {/* SKU */}
                <div className="text-xs text-gray-500 mb-2 font-mono">
                  {product.sku}
                </div>

                {/* Promotion Badge */}
                {product.promotion_reason && (
                  <div className="flex items-center gap-1 mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 border border-orange-300">
                      <StarIconSolid className="h-3 w-3 ml-1" />
                      {product.promotion_reason}
                    </span>
                  </div>
                )}

                {/* New Badge */}
                {product.is_new && (
                  <div className="flex items-center gap-1 text-xs text-green-600 mb-2">
                    <ClockIcon className="h-3 w-3" />
                    <span className="font-semibold">
                      חדש! (לפני {product.days_since_added} ימים)
                    </span>
                  </div>
                )}

                {/* Price */}
                <div className="flex items-center justify-between mt-2">
                  <div>
                    {product.contract_price ? (
                      <div className="flex flex-col">
                        <span className="text-lg font-bold text-blue-600">
                          ₪{product.contract_price}
                        </span>
                        <span className="text-xs text-gray-500 line-through">
                          ₪{product.price_b2b || product.price_b2c}
                        </span>
                      </div>
                    ) : (
                      <span className="text-lg font-bold text-green-600">
                        ₪{product.price_b2b || product.price_b2c}
                      </span>
                    )}
                  </div>

                  {/* Quick Add Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAdd(product);
                    }}
                    className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all transform hover:scale-110"
                    title="הוסף מהר"
                  >
                    <ShoppingCartIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Stock Info */}
                <div className="mt-2 text-xs">
                  {(() => {
                    // Use stock_info if available
                    if (product.stock_info) {
                      const status = product.stock_info.status;
                      
                      // For agents who see exact numbers
                      if (product.stock_info.showExactQuantity && product.stock_info.quantity !== undefined) {
                        const qty = product.stock_info.quantity;
                        const colorClass = qty > 50 
                          ? 'bg-green-100 text-green-700'
                          : qty > 10 
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700';
                        const icon = qty > 50 ? '✅' : qty > 10 ? '⚠️' : '❌';
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-semibold ${colorClass}`}>
                            {icon} {qty} במלאי
                          </span>
                        );
                      }
                      
                      // For regular customers - show status only
                      const colorClass = status.status === 'out_of_stock'
                        ? 'bg-red-100 text-red-700'
                        : status.status === 'low_stock'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700';
                      const icon = status.status === 'out_of_stock' ? '❌' : status.status === 'low_stock' ? '⚠️' : '✅';
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-semibold ${colorClass}`}>
                          {icon} {status.text || 'זמין במלאי'}
                        </span>
                      );
                    }
                    
                    // Fallback
                    const stockQty = product.stock_quantity || 0;
                    const colorClass = stockQty > 50
                      ? 'bg-green-100 text-green-700'
                      : stockQty > 10
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700';
                    const icon = stockQty > 50 ? '✅' : stockQty > 10 ? '⚠️' : '❌';
                    const text = stockQty === 0 ? 'חסר במלאי' : stockQty <= 5 ? 'יחידות אחרונות' : 'זמין במלאי';
                    return (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-semibold ${colorClass}`}>
                        {icon} {text}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer with refresh button */}
      <div className="mt-4 pt-4 border-t border-purple-200 flex justify-center">
        <button
          onClick={fetchFeaturedProducts}
          className="text-xs text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-1"
        >
          <ArrowTrendingUpIcon className="h-4 w-4" />
          רענן המלצות
        </button>
      </div>
    </div>
  );
};

export default FeaturedProductsPanel;

